import { withSentryAssertion } from '@/helpers/withSentryAssertion'
import {
  array,
  bool,
  CheckerReturnType,
  literal,
  nullable,
  number,
  object,
  optional,
  or,
  string,
} from '@recoiljs/refine'

export const BCCurrency = object({
  name: string(),
  code: string(),
  symbol: string(),
  decimalPlaces: number(),
})

export type BCCurrencyType = CheckerReturnType<typeof BCCurrency>

export const BCCartCoupon = object({
  code: string(),
  couponType: string(),
  displayName: string(),
  discountedAmount: number(),
  id: number(),
})

export type BCCartCouponType = CheckerReturnType<typeof BCCartCoupon>

export const BCCartDiscounts = object({
  discountedAmount: number(),
  id: string(),
})

export type BCCartDiscountsType = CheckerReturnType<typeof BCCartDiscounts>

export const BCCartCustomItem = object({
  extendedListPrice: number(),
  id: string(),
  listPrice: number(),
  name: string(),
  quantity: number(),
  sku: string(),
})

export type BCCartCustomItemType = CheckerReturnType<typeof BCCartCustomItem>

export const BCProductOption = object({
  name: string(),
  nameId: number(),
  value: string(),
  valueId: number(),
})

export type BCProductOptionType = CheckerReturnType<typeof BCProductOption>

export const BCAppliedDiscount = object({
  discountedAmount: number(),
  id: string(),
})

export type BCAppliedDiscountType = CheckerReturnType<typeof BCAppliedDiscount>

export const BCCartDigitalItem = object({
  options: optional(array(BCProductOption)),
  brand: string(),
  couponAmount: number(),
  discountAmount: number(),
  discounts: array(BCAppliedDiscount),
  extendedListPrice: number(),
  extendedSalePrice: number(),
  id: string(),
  imageUrl: string(),
  isTaxable: bool(),
  listPrice: number(),
  originalPrice: number(),
  name: string(),
  parentId: optional(nullable(string())),
  productId: or(string(), number()),
  quantity: number(),
  salePrice: number(),
  sku: string(),
  url: string(),
  variantId: number(),
})

export type BCCartDigitalItemType = CheckerReturnType<typeof BCCartDigitalItem>

export const BCContactEntity = object({
  email: string(),
  name: string(),
})

export type BCContactEntityType = CheckerReturnType<typeof BCContactEntity>

export const BCGiftCertificate = object({
  amount: number(),
  id: string(),
  isTaxable: bool(),
  message: string(),
  name: string(),
  recipient: BCContactEntity,
  sender: BCContactEntity,
  theme: string(),
})

export type BCGiftCertificateType = CheckerReturnType<typeof BCGiftCertificate>

export const BCPhysicallItem = object({
  options: optional(array(BCProductOption)),
  brand: string(),
  couponAmount: number(),
  discountAmount: number(),
  discounts: array(object({
    discountedAmount: number(),
    id: string(),
  })),
  extendedListPrice: number(),
  extendedSalePrice: number(),
  id: string(),
  imageUrl: string(),
  isTaxable: bool(),
  listPrice: number(),
  originalPrice: number(),
  name: string(),
  parentId: or(number(), literal(null)),
  productId: number(),
  quantity: number(),
  salePrice: number(),
  sku: string(),
  url: string(),
  variantId: number(),
  giftWrapping: or(literal(null), object({
    amount: number(),
    message: string(),
    name: string(),
  })),
  isShippingRequired: bool(),
  comparisonPrice: optional(number()),
  extendedComparisonPrice: optional(number()),
  addedByPromotion: optional(bool()),
  isMutable: optional(bool()),
})

export type BCPhysicallItemType = CheckerReturnType<typeof BCPhysicallItem>

export const BCCartLineItems = object({
  customItems: array(BCCartCustomItem),
  digitalItems: array(BCCartDigitalItem),
  giftCertificates: array(BCGiftCertificate),
  physicalItems: array(BCPhysicallItem),
})

export type BCCartLineItemsType = CheckerReturnType<typeof BCCartLineItems>

export const BCCart = object({
  id: string(),
  customerId: number(),
  email: nullable(string()),
  currency: BCCurrency,
  isTaxIncluded: bool(),
  baseAmount: number(),
  discountAmount: number(),
  cartAmount: number(),
  coupons: array(BCCartCoupon),
  discounts: array(BCCartDiscounts),
  lineItems: BCCartLineItems,
  createdTime: string(),
  updatedTime: string(),
  locale: string(),
})

export const BCCarts = array(BCCart)

export type BCCartType = CheckerReturnType<typeof BCCart>
export type BCCartsType = CheckerReturnType<typeof BCCarts>

export const assertBCCart = withSentryAssertion([BCCart, 'Bigcommerce Cart'])

export const assertBCCarts = withSentryAssertion([BCCarts, 'Bigcommerce Carts Array'])
